"use client";

import React from "react";
import { MapPin, User, ClipboardList, ShieldCheck, Loader, Check, Info } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
export default function AssessmentForm() {
  return <section id="assessment-form" className="bg-background relative overflow-hidden py-16 md:py-24">
      {}
      <div className="bg-accent/30 absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl" />
      <div className="bg-primary/5 absolute top-1/2 -left-24 h-64 w-64 rounded-full blur-3xl" />

      <div className="container relative mx-auto max-w-4xl px-4">
        <div className="mb-12 text-center">
          <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight md:text-4xl">
            Request Your Professional Assessment
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
            Get a custom-tailored sealing plan for your property. Fill out the details below, and our experts at Island Seals will analyze your needs.
          </p>
        </div>

        <FormProvider formId="assessment_form_request_quote" sectionName="assessment-section" defaultValues={{
        name: "",
        email: "",
        phone: "",
        site_address: "",
        property_type: "",
        surface_type: [],
        area_size: "",
        additional_details: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        site_address: {
          required: true
        },
        property_type: {
          required: true
        },
        surface_type: {
          required: true
        },
        area_size: {
          required: true,
          min: 1
        }
      }} submitText="Protect Now" successMessage="Request received! We will contact you shortly with your assessment.">
          <AssessmentFormContent />
        </FormProvider>
      </div>
    </section>;
}
function AssessmentFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-primary/20 bg-primary/5 mx-auto max-w-lg text-center shadow-lg">
        <CardContent className="pt-10 pb-10">
          <div className="bg-primary/10 text-primary mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full">
            <Check className="h-8 w-8" />
          </div>
          <h3 className="text-foreground mb-2 text-2xl font-bold">Request Submitted</h3>
          <p className="text-muted-foreground mb-6">{successMessage}</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="mx-auto">
            Submit Another Request
          </Button>
        </CardContent>
      </Card>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <Card className="overflow-hidden border-l-4 border-l-blue-500 shadow-md">
        <CardHeader className="bg-muted/30 pb-4">
          <div className="flex items-center gap-3">
            <div className="bg-background flex h-10 w-10 items-center justify-center rounded-full shadow-sm">
              <User className="text-primary h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl">Contact Information</CardTitle>
              <CardDescription>Who should we contact regarding this assessment?</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 pt-6 md:grid-cols-2">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel>Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(555) 123-4567" {...field} />
                </FormControl>
                <FormDescription>Best number to reach you for follow-up.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </CardContent>
      </Card>

      {}
      <Card className="overflow-hidden border-l-4 border-l-cyan-500 shadow-md">
        <CardHeader className="bg-muted/30 pb-4">
          <div className="flex items-center gap-3">
            <div className="bg-background flex h-10 w-10 items-center justify-center rounded-full shadow-sm">
              <MapPin className="text-primary h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl">Property Details</CardTitle>
              <CardDescription>Where is the project located?</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 pt-6 md:grid-cols-2">
          <FormField control={control} name="site_address" render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel>Project / Site Address</FormLabel>
                <FormControl>
                  <Input placeholder="123 Ocean Drive, Miami, FL" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="property_type" render={({
          field
        }) => <FormItem>
                <FormLabel>Property Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Residential">Residential</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Industrial">Industrial</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </CardContent>
      </Card>

      {}
      <Card className="overflow-hidden border-l-4 border-l-teal-500 shadow-md">
        <CardHeader className="bg-muted/30 pb-4">
          <div className="flex items-center gap-3">
            <div className="bg-background flex h-10 w-10 items-center justify-center rounded-full shadow-sm">
              <ClipboardList className="text-primary h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl">Assessment Specifics</CardTitle>
              <CardDescription>Tell us about the surfaces that need protection.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 pt-6">
          <FormField control={control} name="surface_type" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base">Surface Type(s) to Assess</FormLabel>
                <FormDescription className="mb-3">Check all that apply</FormDescription>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {["Concrete", "Stone", "Both", "Other"].map(option => <label key={option} className={`border-input hover:bg-accent hover:text-accent-foreground has-[:checked]:border-primary has-[:checked]:bg-primary/5 has-[:checked]:text-primary flex cursor-pointer flex-col items-center justify-center rounded-lg border p-4 text-center transition-all`}>
                      <input type="checkbox" className="sr-only" checked={field.value?.includes(option)} onChange={e => {
                const checked = e.target.checked;
                const current = field.value || [];
                if (checked) {
                  field.onChange([...current, option]);
                } else {
                  field.onChange(current.filter((v: string) => v !== option));
                }
              }} />
                      <span className="font-medium">{option}</span>
                    </label>)}
                </div>
                <FormMessage />
              </FormItem>} />

          <div className="grid gap-6 md:grid-cols-2">
            <FormField control={control} name="area_size" render={({
            field
          }) => <FormItem>
                  <FormLabel>Approximate Surface Area (sq ft)</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="e.g., 1500" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="additional_details" render={({
          field
        }) => <FormItem>
                <FormLabel>Additional Details / Concerns</FormLabel>
                <FormControl>
                  <textarea className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[100px] w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50" placeholder="Any specific issues, chemicals, or exposure conditions?" {...field} />
                </FormControl>
                <FormDescription>Optional but helpful for a precise assessment.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </CardContent>
      </Card>

      {submitError && <div className="bg-destructive/10 text-destructive flex items-center gap-2 rounded-lg p-4">
          <Info className="h-5 w-5" />
          <p>{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" size="lg" className="w-full text-lg font-semibold shadow-lg transition-all hover:scale-[1.01] md:h-14" disabled={isSubmitting}>
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Processing Request...
            </> : <>
              <ShieldCheck className="mr-2 h-5 w-5" />
              Protect Now
            </>}
        </Button>
        <p className="text-muted-foreground mt-4 text-center text-sm">
          By submitting this form, you agree to our privacy policy and terms of service.
        </p>
      </div>
    </Form>;
}